import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useGetCookieConsentEnabled } from '~/config/firebase/remote-config';
import type { AppState } from '~/reducers';
import { applyAnalyticsConsent } from '../utils/consent-utils';
import { applyClarityConsent } from '../utils/clarity-consent';

const useCookieConsentMode = () => {
  const { data: cookieConsentEnabled, isLoaded } = useGetCookieConsentEnabled();
  const consentState = useSelector((state: AppState) => state.cookieConsent);
  const hasApplied = useRef(false);

  useEffect(() => {
    if (!isLoaded || hasApplied.current) {
      return;
    }

    if (!cookieConsentEnabled) {
      return;
    }

    if (consentState.consentDate === null) {
      // First-time visitor: default-deny all providers until the user decides.
      applyAnalyticsConsent(false);
      applyClarityConsent(false);
    } else {
      // Returning user: Firebase analytics consent was applied at boot via
      // init-consent.ts, but Clarity needs explicit init which may have been
      // skipped (e.g. legacy migration from 'acceptedCookies' key happens in
      // Redux after init-consent.ts runs). Ensure Clarity matches stored state.
      applyClarityConsent(consentState.acceptedCookies);
    }

    hasApplied.current = true;
  }, [isLoaded, cookieConsentEnabled, consentState.consentDate, consentState.acceptedCookies]);
};

export { useCookieConsentMode };
